// The SMTP account form — Gmail App Password or a custom SMTP server.
//
// Used by both /settings/email (tenant) and /platform/email. Validation lives
// in `email-transport-form-logic.ts`; this file is layout, provider switching
// and the read-only mode platform staff / ADMIN see.
//
// The password is NEVER pre-filled: the API only ever returns `••••••••`, and
// any save re-runs verification anyway (plan §5.1), so an edit to just the
// display name still needs the credential re-entered.

import { useEffect, useState } from 'react';
import { Button, Input } from '@bbpos/ui';
import { GLASS_FORM, cn } from '@/constants/design-tokens';
import {
  emptyDraft,
  validateEmailTransportInput,
  type EmailTransportDraft,
  type EmailTransportErrors,
} from './email-transport-form-logic';
import type {
  EmailTransportConfigMasked,
  EmailTransportProvider,
  UpsertEmailTransportInput,
} from '@bbpos/types';

const APP_PASSWORD_URL = 'https://myaccount.google.com/apppasswords';

const PROVIDERS: {
  value: EmailTransportProvider;
  title: string;
  blurb: string;
  gradient: string;
}[] = [
  {
    value: 'gmail',
    title: 'Gmail',
    blurb: 'Your Google account plus a 16-letter App Password. Nothing else to configure.',
    gradient: 'from-danger-500 to-warning-500',
  },
  {
    value: 'smtp',
    title: 'Custom SMTP',
    blurb: 'Any mail server — your host, port and mailbox credentials.',
    gradient: 'from-brand-500 to-indigo-500',
  },
];

function MailIcon({ className = 'h-5 w-5' }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M22 7l-10 6L2 7" />
    </svg>
  );
}

export function EmailTransportForm({
  existing,
  canEdit,
  saving = false,
  onSubmit,
}: {
  existing: EmailTransportConfigMasked | null;
  canEdit: boolean;
  saving?: boolean;
  onSubmit: (input: UpsertEmailTransportInput) => void | Promise<void>;
}) {
  const [draft, setDraft] = useState<EmailTransportDraft>(() => emptyDraft(existing));
  const [errors, setErrors] = useState<EmailTransportErrors>({});
  const [showPassword, setShowPassword] = useState(false);

  // Re-seed once the saved config arrives (or changes under a refetch). The
  // password stays blank by design.
  useEffect(() => {
    setDraft(emptyDraft(existing));
    setErrors({});
  }, [existing]);

  const isGmail = draft.provider === 'gmail';

  const set = (field: keyof EmailTransportDraft, value: string) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const pickProvider = (provider: EmailTransportProvider) => {
    if (!canEdit || provider === draft.provider) return;
    setDraft((prev) => ({ ...prev, provider, smtp_host: '', smtp_port: '' }));
    setErrors({});
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const result = validateEmailTransportInput(draft);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    void onSubmit(result.input);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(GLASS_FORM, 'animate-fade-in space-y-6 p-6')}
      data-testid="email-transport-form"
      noValidate
    >
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-indigo-500 text-white shadow-sm">
          <MailIcon />
        </div>
        <div>
          <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100">Sending account</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Mail goes out from this mailbox. We verify it with a code before using it.
          </p>
        </div>
      </div>

      {/* Provider cards */}
      <div className="grid gap-3 sm:grid-cols-2">
        {PROVIDERS.map((p) => {
          const selected = draft.provider === p.value;
          return (
            <button
              key={p.value}
              type="button"
              role="radio"
              aria-checked={selected}
              disabled={!canEdit}
              onClick={() => pickProvider(p.value)}
              data-testid={`provider-${p.value}`}
              className={cn(
                'rounded-xl border p-4 text-left transition-all duration-200',
                'disabled:cursor-not-allowed disabled:opacity-60',
                selected
                  ? 'border-brand-400 bg-brand-50/60 shadow-md dark:border-brand-500 dark:bg-brand-900/20'
                  : 'border-neutral-200 bg-white hover:border-brand-300 hover:shadow-md dark:border-neutral-700 dark:bg-neutral-800',
              )}
            >
              <div className="flex items-center gap-2">
                <span
                  className={cn(
                    'h-8 w-8 rounded-lg bg-gradient-to-br text-white',
                    p.gradient,
                    'flex items-center justify-center text-xs font-bold',
                  )}
                  aria-hidden="true"
                >
                  {p.title.charAt(0)}
                </span>
                <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                  {p.title}
                </span>
              </div>
              <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">{p.blurb}</p>
            </button>
          );
        })}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {!isGmail && (
          <>
            <Input
              label="SMTP host"
              placeholder="smtp.mybusiness.in"
              value={draft.smtp_host}
              onChange={(v) => set('smtp_host', v)}
              error={errors.smtp_host}
              disabled={!canEdit}
              required
              name="smtp_host"
            />
            <Input
              label="Port"
              type="number"
              placeholder="587"
              value={draft.smtp_port}
              onChange={(v) => set('smtp_port', v)}
              error={errors.smtp_port}
              helperText="465 uses implicit TLS; any other port requires STARTTLS."
              disabled={!canEdit}
              required
              min={1}
              max={65535}
              name="smtp_port"
            />
          </>
        )}

        <Input
          label={isGmail ? 'Gmail address' : 'Mailbox / login email'}
          type="email"
          placeholder="billing@mybusiness.in"
          value={draft.email}
          onChange={(v) => set('email', v)}
          error={errors.email}
          helperText="This is both the SMTP login and the From address."
          disabled={!canEdit}
          required
          name="email"
        />

        <div>
          <Input
            label={isGmail ? 'App Password' : 'Password'}
            type={showPassword ? 'text' : 'password'}
            placeholder={isGmail ? 'abcd efgh ijkl mnop' : '••••••••'}
            value={draft.password}
            onChange={(v) => set('password', v)}
            error={errors.password}
            disabled={!canEdit}
            required
            name="password"
          />
          <div className="mt-1 flex items-center justify-between gap-2">
            {isGmail ? (
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Not your Gmail password —{' '}
                <a
                  href={APP_PASSWORD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-brand-600 underline decoration-dotted hover:text-brand-700 dark:text-brand-400"
                >
                  create an App Password
                </a>{' '}
                (16 letters; spaces are ignored).
              </p>
            ) : (
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                Stored encrypted and never shown again.
              </p>
            )}
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="flex-shrink-0 text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400"
              data-testid="toggle-password"
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        <Input
          label="Display name"
          placeholder="MyBusiness Billing"
          value={draft.from_name}
          onChange={(v) => set('from_name', v)}
          error={errors.from_name}
          helperText="Optional — what recipients see instead of the bare address."
          disabled={!canEdit}
          name="from_name"
        />

        <Input
          label="Reply-To"
          type="email"
          placeholder="support@mybusiness.in"
          value={draft.reply_to_email}
          onChange={(v) => set('reply_to_email', v)}
          error={errors.reply_to_email}
          helperText="Optional — where replies should land."
          disabled={!canEdit}
          name="reply_to_email"
        />
      </div>

      {existing?.last_verify_error && (
        <p className="rounded-lg bg-danger-50 px-3 py-2 text-xs text-danger-700 dark:bg-danger-900/30 dark:text-danger-300">
          Last attempt to connect failed: {existing.last_verify_error}
        </p>
      )}

      {canEdit ? (
        <div className="flex items-center justify-end gap-3">
          <p className="mr-auto text-xs text-neutral-500 dark:text-neutral-400">
            Saving sends a verification code to this mailbox.
          </p>
          <Button type="submit" variant="primary" loading={saving}>
            {existing ? 'Save and re-verify' : 'Save and verify'}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          You have read-only access to this configuration.
        </p>
      )}
    </form>
  );
}
